import * as React from "react";
import { ComponentProps } from "react";
import { cn } from "@repo/utils/cn";
import { Title } from "./Title";
import { List, ListItemType } from "./List";
import { Button } from "./Button";
import { Accordion, AccordionItem } from "./Accordion";
import { SearchField } from "./SearchField";

export interface SectionTitleTypographyItem {
  type: "title" | "text" | "list" | "accordion";
}

export interface SectionTitleTitleItem extends SectionTitleTypographyItem {
  type: "title";
  content: string;
  level: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  fontSize?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  fontColor?: "primary" | "secondary" | "inverted";
  twoToneColor?: "primary" | "secondary" | "inverted";
  textWrap?: "balance" | "pretty" | "normal";
  className?: string;
  [key: string]: any;
}

export interface SectionTitleTextItem extends SectionTitleTypographyItem {
  type: "text";
  content: React.ReactNode;
  size?: "small" | "medium" | "large";
  fontColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
  className?: string;
  [key: string]: any;
}

export interface SectionTitleListItem extends SectionTitleTypographyItem {
  type: "list";
  items: ListItemType[];
  className?: string;
  onItemToggle?: (index: number, title: string, isOpen: boolean) => void;
}

export interface SectionTitleAccordionItem extends SectionTitleTypographyItem {
  type: "accordion";
  items: AccordionItem[];
  className?: string;
  onItemToggle?: (index: number, title: string, isOpen: boolean) => void;
}

export type SectionTitleButtonItem =
  | {
      type: "button";
      text: string;
      variant?:
        | "primary"
        | "secondary"
        | "tertiary"
        | "inverted-fill"
        | "inverted-border";
      size?: "small" | "medium" | "large";
      href?: string;
      target?: string;
      onClick?: () => void;
      analyticsEvent?: "" | "download_app_click" | "ctaClick";
      /** Storyblok `button.type === "navattic"`: opens a Navattic demo instead of navigating. */
      navatticUrl?: string;
      navatticTitle?: string;
      /** Separate mobile flow of the same demo (same title); the desktop one opens without it. */
      navatticMobileUrl?: string;
      disabled?: boolean;
      noFollow?: boolean;
    }
  | {
      type: "searchField";
      placeholder?: string;
      onSearch?: (value: string, resultsCount?: number) => void;
      component?: React.ComponentType<any>;
      analyticsEvent?: "" | "search_location";
    }
  | {
      type: "videoPlayerButton";
      component: React.ComponentType<any>;
    };

export interface SectionTitleProps
  extends Omit<ComponentProps<"div">, "title"> {
  tagText?: string;
  typography?: (
    | SectionTitleTitleItem
    | SectionTitleTextItem
    | SectionTitleListItem
    | SectionTitleAccordionItem
  )[];
  buttons?: SectionTitleButtonItem[];
  layout?: "left" | "center";
}

export const SectionTitle = React.forwardRef<HTMLDivElement, SectionTitleProps>(
  (
    { tagText, typography, buttons, layout = "left", className, ...props },
    ref,
  ) => {
    const alignmentClasses =
      layout === "center"
        ? "uipm:text-center uipm:items-center"
        : "uipm:text-left uipm:items-start";

    const sectionName =
      typography?.find((item) => item.type === "title")?.content || "";

    const firstTitleIndex =
      typography?.findIndex((item) => item.type === "title") ?? -1;

    return (
      <div
        ref={ref}
        className={cn(
          "uipm:flex uipm:flex-col uipm:w-full",
          alignmentClasses,
          className,
        )}
        {...props}
      >
        {tagText && firstTitleIndex === -1 && (
          <p className="uipm:text-[var(--typography-colors-text-inverted)] uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:m-0">
            {tagText}
          </p>
        )}
        <div className="uipm:flex uipm:flex-col uipm:gap-[var(--margin-margin-default)] uipm:w-full">
          {typography?.map((item, index) => {
            if (item.type === "title") {
              return (
                <Title
                  key={index}
                  {...item}
                  tagText={index === firstTitleIndex ? tagText : undefined}
                  textWrap={item.textWrap}
                  className={item.className}
                />
              );
            }
            if (item.type === "text") {
              return item.content;
            }
            if (item.type === "list") {
              return (
                <List
                  key={index}
                  items={item.items}
                  className={cn(
                    "uipm:text-[var(--typography-colors-text-faded-dark)] uipm:w-full uipm:pt-[var(--padding-padding-default)]",
                    item.className,
                  )}
                  onItemToggle={item.onItemToggle}
                />
              );
            }
            if (item.type === "accordion") {
              return (
                <Accordion
                  key={index}
                  items={item.items}
                  className="uipm:w-full uipm:pt-[var(--padding-padding-default)]"
                  onItemToggle={item.onItemToggle}
                />
              );
            }
            return null;
          })}
        </div>

        {buttons && buttons.length > 0 && (
          <div className="uipm:flex uipm:flex-col uipm:w-full uipm:gap-2 uipm:mt-[var(--margin-margin-default)] uipm:pb-[var(--padding-padding-sm-plus)] uipm:md:flex-row uipm:md:gap-[var(--margin-margin-sm)] uipm:md:pb-0">
            {buttons.map((item, index) => {
              if (item.type === "searchField") {
                const SearchComponent = item.component || SearchField;
                return (
                  <div
                    key={index}
                    className="uipm:w-full uipm:md:flex-initial uipm:md:min-w-[260px]"
                  >
                    <SearchComponent
                      placeholder={item.placeholder}
                      onSearch={item.onSearch}
                      className="uipm:w-full"
                      sectionName={sectionName}
                      analyticsEvent={item.analyticsEvent}
                    />
                  </div>
                );
              }

              if (item.type === "videoPlayerButton") {
                const VideoPlayerButtonComponent = item.component;
                return <VideoPlayerButtonComponent key={index} />;
              }

              return (
                <Button
                  key={index}
                  variant={item.variant || "primary"}
                  size={item.size || "medium"}
                  href={item.href}
                  target={item.target}
                  onClick={item.onClick}
                  navatticUrl={item.navatticUrl}
                  navatticTitle={item.navatticTitle}
                  navatticMobileUrl={item.navatticMobileUrl}
                  disabled={item.disabled}
                  noFollow={item.noFollow}
                  className="uipm:flex-[0_0_auto]"
                >
                  {item.text}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

SectionTitle.displayName = "SectionTitle";
